import React, { useEffect, useMemo, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import { FiSearch, FiRefreshCw, FiMail, FiAlertCircle, FiShield, FiClock } from 'react-icons/fi';
import { gatewayApi } from '../services/gateway';

const Logs = () => {
  const [logs, setLogs] = useState([]);
  const [clients, setClients] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [logsResponse, clientsResponse, appsResponse] = await Promise.all([
        gatewayApi.listEmailLogs(200),
        gatewayApi.listClients(),
        gatewayApi.listApps()
      ]);
      setLogs(logsResponse.data || []);
      setClients(clientsResponse.data || []);
      setApps(appsResponse.data || []);
    } catch (err) {
      setError(err.message || 'Unable to load email logs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const clientMap = useMemo(() => Object.fromEntries(clients.map(client => [client.id, client.name])), [clients]);
  const appMap = useMemo(() => Object.fromEntries(apps.map(app => [app.id, app.name])), [apps]);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return logs.filter(log => {
      const statusMatch = statusFilter === 'all' || (log.delivery_status || log.status) === statusFilter;
      const searchMatch = !term || [
        log.subject, log.recipient, log.request_id, log.status, log.error_code,
        clientMap[log.client_id], appMap[log.app_id], log.origin
      ].some(value => String(value || '').toLowerCase().includes(term));
      return statusMatch && searchMatch;
    });
  }, [logs, searchTerm, statusFilter, clientMap, appMap]);

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-10">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Email Logs</h2>
          <p className="text-sm text-slate-500">Gateway requests and Emailit provider delivery events recorded in PocketBase.</p>
        </div>
        <button onClick={load} className="px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-600 flex items-center gap-2 hover:bg-slate-50">
          <SafeIcon icon={FiRefreshCw} /> Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 flex gap-3 text-sm">
          <SafeIcon icon={FiAlertCircle} className="mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-2 flex flex-col md:flex-row gap-2">
        <div className="flex-1 flex items-center gap-2 px-3">
          <SafeIcon icon={FiSearch} className="text-slate-400" />
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search recipient, client, subject, request ID or origin..."
            className="w-full py-2 text-sm outline-none"
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none">
          <option value="all">All Statuses</option>
          <option value="sent">Accepted / Legacy Sent</option><option value="accepted">Accepted</option><option value="delivered">Delivered</option><option value="bounced">Bounced</option><option value="complained">Complained</option>
          <option value="failed">Failed</option>
          <option value="blocked">Blocked</option>
          <option value="honeypot">Honeypot</option>
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto custom-scrollbar">
          <table className="min-w-full text-left whitespace-nowrap">
            <thead>
              <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-bold border-b border-slate-100">
                <th className="px-5 py-4">Time</th>
                <th className="px-5 py-4">Client / App</th>
                <th className="px-5 py-4">Recipient</th>
                <th className="px-5 py-4">Subject</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4">Request</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {loading ? (
                <tr><td colSpan="6" className="px-5 py-12 text-center text-slate-400">Loading PocketBase email logs...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan="6" className="px-5 py-12 text-center text-slate-400">No email logs found.</td></tr>
              ) : filtered.map(log => (
                <tr key={log.id} className="hover:bg-slate-50/70">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <SafeIcon icon={FiClock} />
                      {formatDate(log.sent_at || log.created)}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <p className="font-bold text-slate-800">{clientMap[log.client_id] || 'Unknown Client'}</p>
                    <p className="text-xs text-slate-500">{appMap[log.app_id] || 'Standalone'}</p>
                  </td>
                  <td className="px-5 py-4 text-slate-600">{log.recipient || '—'}</td>
                  <td className="px-5 py-4 max-w-[260px] truncate text-slate-700">{log.subject || '—'}</td>
                  <td className="px-5 py-4"><StatusBadge status={log.delivery_status || log.status} /></td>
                  <td className="px-5 py-4">
                    <p className="font-mono text-[10px] text-slate-500">{log.request_id || '—'}</p>
                    {log.error_code && <p className="text-[10px] text-red-600 font-bold mt-1">{log.error_code}</p>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3 text-xs text-blue-800">
        <SafeIcon icon={FiShield} className="text-blue-600 mt-0.5" />
        <span>Accepted means Emailit received the message; delivered means the destination mail server accepted it. A delivered email may still land in spam.</span>
      </div>
    </div>
  );
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('en-AU');
};

const StatusBadge = ({ status }) => {
  const value = String(status || '').toLowerCase();
  const styles = ['sent','delivered'].includes(value)
    ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
    : ['failed','bounced','rejected','complained'].includes(value)
      ? 'bg-red-50 text-red-700 border-red-100'
      : value === 'honeypot'
        ? 'bg-purple-50 text-purple-700 border-purple-100'
        : 'bg-amber-50 text-amber-700 border-amber-100';
  const icon = ['sent','delivered'].includes(value) ? FiMail : FiAlertCircle;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase border ${styles}`}>
      <SafeIcon icon={icon} /> {value || 'unknown'}
    </span>
  );
};

export default Logs;